import { Link } from 'react-router-dom';

import { formatDate, formatTime } from '../../lib/format';
import { ReliabilityBadge } from '../../components/ReliabilityBadge';
import { HeroMedia } from '../../components/HeroMedia';
import { useEventContext } from '../context';
import { OpportunityCard } from '../../pages/public/OpportunityCard';
import { useHeroMedia } from '../heroMedia';
import { AdmissionSurface } from './AdmissionSurface';

/**
 * The Event blocks.
 *
 * ── THESE RENDER THE EXACT MARKUP THE HARDCODED PAGE RENDERS ─────────────────────────
 *
 * Same elements, same class names, same order, same conditionals. That is deliberate and
 * it is what makes "the CMS version looks identical" true by construction rather than by
 * careful comparison: the legacy page and the CMS template are two compositions of the
 * SAME components, so there is no second implementation to drift.
 *
 * ── THEY READ CONTEXT, THEY DO NOT FETCH ─────────────────────────────────────────────
 *
 * Every figure below — prices, remaining inventory, buyability — comes from the event the
 * host already loaded from the authoritative endpoint. Nothing about availability lives
 * in a CMS document, so no stale configuration can authorize a purchase.
 */

export function EventHero({
  mediaAssetId = null, videoMediaAssetId = null, posterMediaAssetId = null,
  showDate = true, showVenue = true, showFlags = true,
}: {
  mediaAssetId?: string | null;
  videoMediaAssetId?: string | null;
  posterMediaAssetId?: string | null;
  showDate?: boolean;
  showVenue?: boolean;
  showFlags?: boolean;
}) {
  const { event } = useEventContext();

  // The whole media decision, in one deterministic chain — see cms/heroMedia.ts. A CMS
  // asset that cannot be resolved (which is every one of them until object storage is
  // configured) falls through to the event's own artwork rather than blanking the hero.
  const media = useHeroMedia(event, { mediaAssetId, videoMediaAssetId, posterMediaAssetId });

  const time = formatTime(event.time);
  const doors = formatTime(event.doorsTime);
  const venueLine = [event.venue.name, event.venue.address, event.venue.city, event.venue.state]
    .filter(Boolean)
    .join(', ');

  return (
    <header className="detail__hero">
      <div className="detail__hero-media">
        <HeroMedia
          imageUrl={media.imageUrl}
          videoUrl={media.videoUrl}
          videoWebmUrl={media.videoWebmUrl}
        />
      </div>

      <div className="detail__hero-body">
        {showDate && (
          <p className="detail__date mono">
            {formatDate(event.date)}
            {time ? ` · ${time}` : ''}
          </p>
        )}
        <h1 className="detail__title display">{event.title}</h1>
        {showVenue && venueLine && <p className="detail__venue">{venueLine}</p>}

        {showFlags && (
          <div className="detail__flags">
            {event.complianceBadge === 'fully_permitted' && (
              <span
                className="badge badge--verified"
                title="Every permit and compliance item for this event has been verified by Voleto."
              >
                ✓ Fully permitted
              </span>
            )}
            {event.genre && <span className="badge">{event.genre}</span>}
            {event.ageRestriction && <span className="badge">{event.ageRestriction}</span>}
            {doors && <span className="badge">Doors {doors}</span>}
          </div>
        )}
      </div>
    </header>
  );
}

export function EventDescription({ heading = 'About' }: { heading?: string }) {
  const { event } = useEventContext();
  if (!event.description) return null;
  return (
    <section className="detail__section">
      <h2 className="detail__section-title">{heading}</h2>
      <p className="detail__description">{event.description}</p>
    </section>
  );
}

export function EventLineup({
  heading = 'Lineup', showReliability = true,
}: { heading?: string; showReliability?: boolean }) {
  const { event } = useEventContext();
  if (event.lineup.length === 0) return null;
  return (
    <section className="detail__section">
      <h2 className="detail__section-title">{heading}</h2>
      <ul className="lineup">
        {event.lineup.map((artist) => (
          <li key={artist.artistId} className="lineup__item">
            <Link to={`/artists/${artist.artistId}`} className="lineup__link">
              <span className="lineup__avatar" aria-hidden="true">
                {artist.profileImageUrl ? (
                  <img src={artist.profileImageUrl} alt="" />
                ) : (
                  artist.stageName.slice(0, 1)
                )}
              </span>
              <span className="lineup__meta">
                <span className="lineup__name">{artist.stageName}</span>
                {artist.genre && <span className="lineup__genre">{artist.genre}</span>}
              </span>
            </Link>
            {showReliability && (
              <ReliabilityBadge
                score={artist.reliabilityScore}
                buildingTrackRecord={artist.buildingTrackRecord}
                size="md"
              />
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * The ticket panel. CRITICAL: if it throws, the page fails loudly rather than rendering
 * an event that looks fine and sells nothing.
 *
 * Wording comes from the Copy Layer, because it is the same wording wherever tickets are
 * offered. Whether the button is enabled comes from inventory, and no document or
 * override can reach that.
 */
export function EventTickets() {
  const { event } = useEventContext();

  // ── STILL THE CRITICAL BLOCK, NOW MODE-AWARE ──────────────────────────────
  //
  // §34/§35. The registry entry, its name, its version and its `critical` flag are all
  // unchanged, so the publish invariant — exactly one of these on an event page — still
  // holds and a page still cannot be published with no way in. What changed is that the
  // panel renders the admission surface the EVENT is configured for: tiers, an RSVP, a
  // request, a waitlist or an invitation prompt.
  //
  // A versioned rename to EventAdmissions is a deliberate future step. Doing it here would
  // have meant migrating every published document to keep the invariant that stops a page
  // silently having no purchase surface — in exchange for a better name.
  return <AdmissionSurface event={event} />;
}

export function EventOpportunity() {
  const { opportunity } = useEventContext();
  if (!opportunity) return null;
  return (
    <div className="detail__opportunity">
      <OpportunityCard opportunity={opportunity} />
    </div>
  );
}
